/** Single global Snackbar via Context. `useSnackbar().show(msg, severity, opts?)`. */
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, IconButton, Snackbar as MuiSnackbar } from '@mui/material';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type Severity = 'success' | 'error' | 'info' | 'warning';

export interface SnackbarAction {
  label: string;
  onClick: () => void;
}

export interface SnackbarShowOptions {
  /** Override the default 5s auto-hide (e.g. 20000 for a download offer). */
  autoHideDuration?: number;
  /** Optional action button rendered inside the Alert. */
  action?: SnackbarAction;
  /** Several action buttons, rendered left to right. Wins over `action`. */
  actions?: SnackbarAction[];
  /** Opt-in: a click elsewhere on the page does not dismiss it (an Undo / Restore the
   *  operator may still need). It still hides after its time, or with ✕. */
  ignoreClickAway?: boolean;
}

interface SnackbarMsg {
  message: string;
  severity: Severity;
  autoHideDuration: number;
  actions: SnackbarAction[];
  ignoreClickAway: boolean;
}

interface SnackbarCtx {
  show: (message: string, severity?: Severity, opts?: SnackbarShowOptions) => void;
}

const Ctx = createContext<SnackbarCtx | null>(null);

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<SnackbarMsg | null>(null);

  const show = useCallback(
    (message: string, severity: Severity = 'info', opts?: SnackbarShowOptions) => {
      setMsg({
        message,
        severity,
        autoHideDuration: opts?.autoHideDuration ?? 5000,
        actions: opts?.actions ?? (opts?.action ? [opts.action] : []),
        ignoreClickAway: opts?.ignoreClickAway ?? false,
      });
    },
    [],
  );

  const value = useMemo<SnackbarCtx>(() => ({ show }), [show]);
  const close = () => setMsg(null);

  return (
    <Ctx.Provider value={value}>
      {children}
      <MuiSnackbar
        open={Boolean(msg)}
        autoHideDuration={msg?.autoHideDuration ?? 5000}
        onClose={(_e, reason) => {
          if (reason === 'clickaway' && msg?.ignoreClickAway) return;
          close();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {msg ? (
          <Alert
            severity={msg.severity}
            variant="filled"
            // A custom `action` replaces the close icon MUI derives from
            // onClose, so when any are present we render every button ourselves.
            {...(msg.actions.length
              ? {
                  action: (
                    <>
                      {msg.actions.map((a) => (
                        <Button
                          key={a.label}
                          color="inherit"
                          size="small"
                          onClick={() => {
                            a.onClick();
                            close();
                          }}
                        >
                          {a.label}
                        </Button>
                      ))}
                      <IconButton color="inherit" size="small" aria-label="close" onClick={close}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </>
                  ),
                }
              : { onClose: close })}
          >
            {msg.message}
          </Alert>
        ) : undefined}
      </MuiSnackbar>
    </Ctx.Provider>
  );
}

export function useSnackbar(): SnackbarCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSnackbar must be used inside <SnackbarProvider>');
  return v;
}
